import AdminAPI from '../../services/AdminAPI';

export const STORAGE_KEYS = {
  identity: "school_identity_data",
  schoolInfo: "school_information_data",
  teams: "school_teams_committees_data",
  market: "school_market_dynamics_data",
  diagnosis: "school_diagnosis_data",
  swot: "school_swot_data",
  goals: "school_goals_data",
  actions: "school_action_plans_data",
  monitoring: "school_monitoring_data",
  reports: "school_reports_summary_data",
  compiled: "school_complete_view_data",
  expenditure: "school_expenditure_data",
  settings: "school_portal_settings",
  activity: "school_portal_activity_log",
  sqaafCompletionCache: "sqaaf_completion_cache",
};

export const MODULES = [
  { key: "identity", name: "Institutional Identity", file: "/sdp/institutional-identity", description: "Vision, mission, values, culture, ultimate outcome, revision history" },
  { key: "schoolInfo", name: "School Information", file: "/sdp/school-information", description: "Basic school profile, board, classes, student and staff snapshot" },
  { key: "expenditure", name: "Expenditure", file: "/sdp/expenditure", description: "Year-wise planned expenditure, budget, spend, and operational priorities" },
  { key: "teams", name: "Teams & Committees", file: "/sdp/teams-committees", description: "Leadership roles, committees, coordinators, ownership structure" },
  { key: "market", name: "Market Dynamics", file: "/sdp/market-dynamics", description: "Parent expectations, competition, external environment, local trends" },
  { key: "diagnosis", name: "Diagnosis", file: "/sdp/diagnosis", description: "Current school condition, issues, baseline observations" },
  { key: "swot", name: "SWOT", file: "/sdp/swot", description: "Strengths, weaknesses, opportunities, threats" },
  { key: "goals", name: "Goals", file: "/sdp/goals", description: "Strategic goals, timelines, priority areas, indicators" },
  { key: "actions", name: "Action Plans", file: "/sdp/action-plans", description: "Execution plans, ownership, deadlines, dependencies" },
  { key: "monitoring", name: "Monitoring", file: "/sdp/monitoring", description: "Review cycles, progress logs, evidence, checkpoints" },
  { key: "reports", name: "Reports & Summary", file: "/sdp/reports-summary", description: "Module summary, readiness view, final reporting layer" },
  { key: "compiled", name: "Complete School View", file: "/sdp/complete-school-view", description: "One compiled screen pulling all module data together" },
];

// SDP data is tenant-specific. Every cache key must be namespaced by the
// current school_id, otherwise switching between schools in the same browser
// (or spinning up a new school/branch after viewing another one) shows the
// previous tenant's cached SDP data before/instead of the real (empty) data
// for the new school.
function getCurrentSchoolId() {
  try {
    return localStorage.getItem("school_id") || "no-school";
  } catch {
    return "no-school";
  }
}

function scopedKey(key) {
  return `sdp:${getCurrentSchoolId()}:${key}`;
}

export function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(scopedKey(key));
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function saveStorage(key, value) {
  localStorage.setItem(scopedKey(key), JSON.stringify(value));
}

export function addActivity(module, action, status = "Saved", statusClass = "green") {
  const activity = readStorage(STORAGE_KEYS.activity, []);
  activity.push({ time: new Date().toLocaleString(), module, action, status, statusClass });
  saveStorage(STORAGE_KEYS.activity, activity);
}

export function calculateIdentityCompletion(data) {
  const fields = [data.vision, data.mission, data.supportingLine, data.versionType];
  const valuesCount = Array.isArray(data.values) && data.values.length > 0 ? 1 : 0;
  const cultureCount = data.cultureStatement ? 1 : 0;
  const max = fields.length + 2;
  const filled = fields.filter(Boolean).length + valuesCount + cultureCount;
  return Math.round((filled / max) * 100);
}

export function calculateObjectCompletion(obj, requiredFields) {
  if (!obj) return 0;
  const total = requiredFields.length;
  const filled = requiredFields.filter(field => {
    const value = obj[field];
    return value !== undefined && value !== null && String(value).trim() !== "";
  }).length;
  return Math.round((filled / total) * 100);
}

export function calculateArrayCompletion(arr, idealCount) {
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  const ratio = Math.min(arr.length / idealCount, 1);
  return Math.round(ratio * 100);
}

export function calculateSwotCompletion(data) {
  if (!data) return 0;
  // SWOT saves as { points: [{quadrant, priority, ...}] }
  const points = Array.isArray(data.points) ? data.points : [];
  const strengths     = points.filter(p => p.quadrant === "Strength").length;
  const weaknesses    = points.filter(p => p.quadrant === "Weakness").length;
  const opportunities = points.filter(p => p.quadrant === "Opportunity").length;
  const threats       = points.filter(p => p.quadrant === "Threat").length;
  const highPriority  = points.filter(p => p.priority === "High").length;
  let filled = 0;
  if (strengths > 0)     filled++;
  if (weaknesses > 0)    filled++;
  if (opportunities > 0) filled++;
  if (threats > 0)       filled++;
  if (points.length >= 4) filled++;
  if (points.length >= 6) filled++;
  if (highPriority > 0)  filled++;
  if (highPriority >= 2) filled++;
  return Math.round((filled / 8) * 100);
}

export function getModuleCompletion(moduleKey) {
  // "reports" and "compiled" are derived views — they have no stored data of their own
  // and must be computed before the null-data guard below.
  if (moduleKey === "reports") {
    const coreKeys = ["identity","schoolInfo","teams","market","diagnosis","swot","goals","actions","monitoring"];
    const vals = coreKeys.map(k => getModuleCompletion(k));
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }
  if (moduleKey === "compiled") {
    const completed = MODULES.filter(m => m.key !== "compiled" && m.key !== "reports")
      .filter(m => getModuleCompletion(m.key) >= 60).length;
    const total = MODULES.filter(m => m.key !== "compiled" && m.key !== "reports").length;
    return Math.round((completed / total) * 100);
  }

  const data = readStorage(STORAGE_KEYS[moduleKey], null);
  if (!data) return 0;
  switch (moduleKey) {
    case "identity": return calculateIdentityCompletion(data);
    case "schoolInfo": return calculateObjectCompletion(data, ["schoolName","schoolCode","board","academicYear","principalName","classesOffered","studentStrength","staffStrength"]);
    case "teams": {
      const leadership  = Array.isArray(data.leadership)  ? data.leadership  : [];
      const committees  = Array.isArray(data.committees)  ? data.committees  : [];
      let filled = 0;
      if (leadership.length > 0)  filled++;
      if (leadership.length >= 3) filled++;
      if (committees.length > 0)  filled++;
      if (committees.length >= 3) filled++;
      if (data.mainOwner)          filled++;
      if (data.reviewFrequency)    filled++;
      if (data.escalationLevel1)   filled++;
      if (data.ownershipNote)      filled++;
      return Math.round((filled / 8) * 100);
    }
    case "market": {
      const comps = Array.isArray(data.competitors) ? data.competitors : [];
      const sigs  = Array.isArray(data.signals)     ? data.signals     : [];
      let filled = 0;
      if (data.parentExpectations) filled++;
      if (data.communityNeeds)     filled++;
      if (data.admissionsTrend)    filled++;
      if (data.futureDemand)       filled++;
      if (data.demographicNote)    filled++;
      if (comps.length > 0)        filled++;
      if (comps.length >= 2)       filled++;
      if (sigs.length > 0)         filled++;
      if (sigs.length >= 2)        filled++;
      return Math.round((filled / 9) * 100);
    }
    case "diagnosis": {
      const findings = Array.isArray(data.findings) ? data.findings : [];
      let filled = 0;
      if (findings.length > 0)                                   filled += 2;
      if (findings.length >= 4)                                  filled += 2;
      if (findings.filter(f => f.description).length >= 2)      filled += 2;
      if (findings.filter(f => f.severity).length >= 2)         filled++;
      if (findings.filter(f => f.urgency).length >= 2)          filled++;
      const manualPct = Math.round((filled / 8) * 100);
      // SQAAF completion is a valid school diagnosis — use whichever is higher
      const sqaafCache = readStorage(STORAGE_KEYS.sqaafCompletionCache, null);
      const sqaafPct = typeof sqaafCache?.percent === "number" ? sqaafCache.percent : 0;
      return Math.max(manualPct, sqaafPct);
    }
    case "swot": return calculateSwotCompletion(data);
    case "goals": return calculateArrayCompletion(data.goals || [], 3);
    case "actions": return calculateArrayCompletion(data.actions || [], 3);
    case "monitoring": {
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];
      const total        = reviews.length;
      const withGoal     = reviews.filter(r => r.goal).length;
      const withAction   = reviews.filter(r => r.action).length;
      const withRisks    = reviews.filter(r => r.blocker).length;
      const withNext     = reviews.filter(r => r.nextReviewDate).length;
      const onTrack      = reviews.filter(r => r.status === "On Track").length;
      let filled = 0;
      if (total > 0)                                              filled++;
      if (total >= 3)                                             filled++;
      if (withGoal >= Math.min(total, 2) && total > 0)           filled++;
      if (withAction >= Math.min(total, 2) && total > 0)         filled++;
      if (withNext > 0)                                           filled++;
      if (withRisks > 0)                                          filled++;
      if (onTrack > 0 || total > 0)                              filled++;
      if (total >= 5)                                             filled++;
      return Math.round((filled / 8) * 100);
    }
    default: return 0;
  }
}

export function getStatusByCompletion(percent) {
  if (percent >= 100) return { label: "Completed", cls: "green" };
  if (percent >= 60) return { label: "In Progress", cls: "blue" };
  if (percent > 0) return { label: "Partially Filled", cls: "orange" };
  return { label: "Not Started", cls: "yellow" };
}

export function buildRingStyle(percent) {
  const deg = Math.round((percent / 100) * 360);
  return `conic-gradient(#2d81c1 0deg ${deg}deg, #edf2f7 ${deg}deg 360deg)`;
}

export function getSchoolSettings() {
  return readStorage(STORAGE_KEYS.settings, { schoolName: "ABC Public School", academicYear: "2026–27" });
}

export function getSchoolInitials(name) {
  return (name || "ABC Public School").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "SP";
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getActiveAcademicYear() {
  return readStorage(STORAGE_KEYS.settings, { academicYear: '2026-27' }).academicYear || '2026-27';
}

export function clearYearCache() {
  Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(scopedKey(k)));
}

export async function switchActiveYear(year) {
  clearYearCache();
  saveStorage(STORAGE_KEYS.settings, { ...getSchoolSettings(), academicYear: year });
}

// ── Async API wrappers (localStorage as cache/fallback) ───────────────────────

export async function loadModuleData(moduleKey, fallback = null) {
  const year = getActiveAcademicYear();
  try {
    const res = await AdminAPI.sdpGetModule(moduleKey, year);
    if (res?.data) {
      saveStorage(STORAGE_KEYS[moduleKey], res.data);
      return res.data;
    }
  } catch (err) {
    if (!String(err?.message || err).includes('404')) {
      console.warn(`SDP load failed for ${moduleKey}:`, err?.message || err);
    }
  }
  return readStorage(STORAGE_KEYS[moduleKey], fallback);
}

export async function saveModuleData(moduleKey, data, completionPercent = 0) {
  const year = getActiveAcademicYear();
  saveStorage(STORAGE_KEYS[moduleKey], data);
  try {
    const sqaafAuditId = data?.sqaaf_audit_id || null;
    await AdminAPI.sdpSaveModule(moduleKey, year, data, completionPercent, sqaafAuditId);
  } catch (err) {
    console.warn(`SDP save failed for ${moduleKey}, retained locally:`, err?.message || err);
  }
}

export async function addActivityAsync(module, action, status = 'Saved', statusClass = 'green') {
  addActivity(module, action, status, statusClass);
  try {
    await AdminAPI.sdpAppendActivity(module, action, status, statusClass);
  } catch (err) {
    console.warn('SDP activity API failed:', err?.message || err);
  }
}

export async function loadActivityLog(limit = 100) {
  try {
    const res = await AdminAPI.sdpGetActivity(limit);
    if (Array.isArray(res)) {
      const normalised = res.map(e => ({
        ...e,
        statusClass: e.status_class || 'blue',
        time: new Date(e.time).toLocaleString(),
      }));
      saveStorage(STORAGE_KEYS.activity, normalised);
      return normalised;
    }
  } catch (err) {
    console.warn('SDP activity load failed:', err?.message || err);
  }
  return readStorage(STORAGE_KEYS.activity, []);
}

export async function loadSettings() {
  try {
    const res = await AdminAPI.sdpGetSettings();
    if (res?.schoolName) {
      saveStorage(STORAGE_KEYS.settings, res);
      return res;
    }
  } catch (err) {
    console.warn('SDP settings load failed:', err?.message || err);
  }
  return getSchoolSettings();
}

export async function saveSettings(schoolName, academicYear) {
  saveStorage(STORAGE_KEYS.settings, { schoolName, academicYear });
  try {
    await AdminAPI.sdpSaveSettings(schoolName, academicYear);
  } catch (err) {
    console.warn('SDP settings save failed:', err?.message || err);
  }
}

export async function loadGoals(fallback = []) {
  const year = getActiveAcademicYear();
  try {
    const res = await AdminAPI.sdpGetGoals(year);
    if (res?.goals) {
      const goals = res.goals.map(g => ({ ...g, title: g.title || g.goal || "", goal: g.goal || g.title || "" }));
      const cached = { goals };
      if (res.sqaaf_audit_id) cached.sqaaf_audit_id = res.sqaaf_audit_id;
      saveStorage(STORAGE_KEYS.goals, cached);
      return goals;
    }
  } catch (err) {
    if (!String(err?.message || err).includes('404')) {
      console.warn('SDP goals load failed:', err?.message || err);
    }
  }
  const cached = readStorage(STORAGE_KEYS.goals, null);
  return cached?.goals || fallback;
}

export async function saveGoals(goals, sqaafAuditId = null) {
  const year = getActiveAcademicYear();
  const cached = { goals };
  if (sqaafAuditId) cached.sqaaf_audit_id = sqaafAuditId;
  saveStorage(STORAGE_KEYS.goals, cached);
  try {
    await AdminAPI.sdpSaveGoals(year, goals, sqaafAuditId);
  } catch (err) {
    console.warn('SDP goals save failed:', err?.message || err);
  }
}

export async function generateGoalsFromActions() {
  const year = getActiveAcademicYear();
  try {
    return await AdminAPI.sdpGenerateGoals(year);
  } catch (err) {
    console.warn('SDP goal generation failed:', err?.message || err);
    return [];
  }
}

export async function loadActions(fallback = []) {
  const year = getActiveAcademicYear();
  try {
    const res = await AdminAPI.sdpGetActions(year);
    if (res?.actions) {
      saveStorage(STORAGE_KEYS.actions, { actions: res.actions });
      return res.actions;
    }
  } catch (err) {
    if (!String(err?.message || err).includes('404')) {
      console.warn('SDP actions load failed:', err?.message || err);
    }
  }
  const cached = readStorage(STORAGE_KEYS.actions, null);
  return cached?.actions || fallback;
}

export async function saveActions(actions) {
  const year = getActiveAcademicYear();
  saveStorage(STORAGE_KEYS.actions, { actions });
  try {
    await AdminAPI.sdpSaveActions(year, actions);
  } catch (err) {
    console.warn('SDP actions save failed:', err?.message || err);
  }
}

// ── SQAAF Integration helpers ─────────────────────────────────────────────────

export async function getSQAAFStatus() {
  const year = getActiveAcademicYear();
  try {
    return await AdminAPI.sdpGetSQAAFStatus(year);
  } catch (err) {
    console.warn('SDP sqaaf-status failed:', err?.message || err);
    return null;
  }
}

export async function generateGoalsFromSQAAF() {
  const year = getActiveAcademicYear();
  try {
    return await AdminAPI.sdpGenerateGoalsFromSQAAF(year);
  } catch (err) {
    console.warn('SDP goals/generate-from-sqaaf failed:', err?.message || err);
    return null;
  }
}

export async function generateSWOTFromSQAAF() {
  const year = getActiveAcademicYear();
  try {
    return await AdminAPI.sdpGenerateSWOTFromSQAAF(year);
  } catch (err) {
    console.warn('SDP swot/generate-from-sqaaf failed:', err?.message || err);
    return null;
  }
}

export async function generateMarketInsightsFromIdentity() {
  const year = getActiveAcademicYear();
  try {
    return await AdminAPI.sdpGenerateMarketInsightsFromIdentity(year);
  } catch (err) {
    console.warn('SDP market/generate-from-identity failed:', err?.message || err);
    return null;
  }
}

export async function generateIdentityDraft(schoolName, schoolType, description) {
  try {
    return await AdminAPI.sdpGenerateIdentityDraft(schoolName, schoolType, description);
  } catch (err) {
    console.warn('SDP identity/generate-draft failed:', err?.message || err);
    return null;
  }
}

export async function generateQuickStartDraft(schoolName, schoolType, description) {
  try {
    return await AdminAPI.sdpGenerateQuickStartDraft(schoolName, schoolType, description);
  } catch (err) {
    console.warn('SDP quick-start/generate failed:', err?.message || err);
    return null;
  }
}
